import { useMemo } from 'react'
import type { Message } from '@/api/types'
import { conversationKey, isLocalId, useChatStore, type LiveUserMessage } from '@/stores/chatStore'
import { AssistantMessage } from './AssistantMessage'
import { UserMessage } from './UserMessage'

type Row =
  | { kind: 'user'; key: string; message?: Message; live?: LiveUserMessage }
  | { kind: 'assistant'; key: string; message?: Message; streamId?: string }

type Props = {
  conversationId: string | null
  /** Persisted messages from react-query (undefined while loading). */
  messages: Message[] | undefined
  onEdited: () => void
}

/**
 * Merges persisted messages with the live turn. Rows keep the same React key
 * from first token to persisted message (via `clientKeys`), so committing a turn
 * never remounts it. Only the streaming assistant row subscribes to deltas.
 */
export function MessageList({ conversationId, messages, onEdited }: Props) {
  const key = conversationKey(conversationId)
  const streamId = useChatStore((s) => s.byConversation[key])
  const liveUser = useChatStore((s) => (streamId ? s.turns[streamId]?.user : undefined))
  const truncateAt = useChatStore((s) => (streamId ? (s.turns[streamId]?.truncateAt ?? null) : null))
  const liveAssistantKey = useChatStore((s) => (streamId ? s.turns[streamId]?.assistant.clientId : undefined))
  const liveAssistantId = useChatStore((s) => (streamId ? (s.turns[streamId]?.assistant.id ?? null) : null))
  const streaming = useChatStore((s) => (streamId ? s.turns[streamId]?.assistant.status === 'streaming' : false))
  const clientKeys = useChatStore((s) => s.clientKeys)

  const rows = useMemo(() => {
    let base = messages ?? []
    if (truncateAt) {
      const index = base.findIndex((m) => m.id === truncateAt)
      if (index !== -1) base = base.slice(0, index)
    }
    const baseIds = new Set(base.map((m) => m.id))
    const result: Row[] = base.map((m) =>
      m.role === 'user'
        ? { kind: 'user', key: clientKeys[m.id] ?? m.id, message: m }
        : { kind: 'assistant', key: clientKeys[m.id] ?? m.id, message: m },
    )
    if (streamId) {
      // Prefer the persisted copy once it exists (same key, so no remount).
      if (liveUser && !baseIds.has(liveUser.id ?? liveUser.clientId)) {
        result.push({ kind: 'user', key: liveUser.clientId, live: liveUser })
      }
      if (liveAssistantKey && !baseIds.has(liveAssistantId ?? liveAssistantKey)) {
        result.push({ kind: 'assistant', key: liveAssistantKey, streamId })
      }
    }
    return result
  }, [messages, truncateAt, clientKeys, streamId, liveUser, liveAssistantKey, liveAssistantId])

  let lastAssistant = -1
  rows.forEach((row, index) => {
    if (row.kind === 'assistant') lastAssistant = index
  })

  return (
    <div className="mx-auto w-full max-w-reading space-y-9 px-4 pb-20 pt-6 sm:px-6">
      {rows.map((row, index) => {
        if (row.kind === 'user') {
          const id = row.message?.id ?? row.live?.id ?? null
          const serverId = id && !isLocalId(id) ? id : null
          return (
            <UserMessage
              key={row.key}
              messageId={serverId}
              content={row.message?.content ?? row.live?.content ?? ''}
              conversationId={conversationId}
              canEdit={!streaming && Boolean(conversationId && serverId)}
              onEdited={onEdited}
              animateIn={Boolean(row.live)}
            />
          )
        }
        return (
          <AssistantMessage
            key={row.key}
            rowKey={row.key}
            message={row.message}
            streamId={row.streamId}
            conversationId={conversationId}
            isLast={index === lastAssistant && index === rows.length - 1}
            canAct={!streaming}
            animateIn={Boolean(row.streamId)}
          />
        )
      })}
    </div>
  )
}
